import { useEffect, useMemo, useState } from 'react'

import {
  type EpisodeGeneratePagePickRow,
  listEpisodeGeneratePagePickRows,
  selectablePagePickRows,
} from '../../lib/comicEpisodeGeneratePagePick'
import { getActiveEpisode } from '../../lib/comicProjectMutations'
import type { ComicProject } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ChromeMetaTag } from '../ChromeTags'
import { ThemeModal } from '../modal'
import { ComicPageImageStatusTag } from './ComicPageImageStatusTag'

type Props = {
  open: boolean
  angular: boolean
  project: ComicProject | null
  episodeId: string | null
  onClose: () => void
  onConfirm: (pageIds: string[]) => void
}

export function ComicEpisodeGeneratePagePickModal({
  open,
  angular,
  project,
  episodeId,
  onClose,
  onConfirm,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const episode = useMemo(() => {
    if (!project || !episodeId) return null
    try {
      return getActiveEpisode(project, episodeId)
    } catch {
      return null
    }
  }, [project, episodeId])

  const rows = useMemo(
    () => (episode ? listEpisodeGeneratePagePickRows(episode) : []),
    [episode],
  )

  const selectableRows = useMemo(() => selectablePagePickRows(rows), [rows])

  useEffect(() => {
    if (!open) {
      setSelectedIds([])
      return
    }
    setSelectedIds([])
  }, [open, episodeId])

  const togglePage = (row: EpisodeGeneratePagePickRow) => {
    if (!row.selectable) return
    setSelectedIds((cur) =>
      cur.includes(row.pageId) ? cur.filter((id) => id !== row.pageId) : [...cur, row.pageId],
    )
  }

  const selectAllGeneratable = () => {
    setSelectedIds(selectableRows.map((r) => r.pageId))
  }

  const selectWithoutApplied = () => {
    setSelectedIds(selectableRows.filter((r) => !r.hasAppliedImage).map((r) => r.pageId))
  }

  const clearSelection = () => setSelectedIds([])

  const cardRound = angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]'

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title={episode ? `选择要生成的页 · ${episode.title}` : '选择要生成的页'}
      angular={angular}
      size="md"
      bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-ink-soft">
            已选 <span className="font-semibold tabular-nums text-ink">{selectedIds.length}</span>{' '}
            页
            {selectableRows.length > 0 ? (
              <span className="text-ink-soft">
                {' '}
                / 可生成 {selectableRows.length} 页
              </span>
            ) : null}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={onClose}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton
              density="compact"
              type="button"
              disabled={selectedIds.length === 0}
              onClick={() => {
                if (selectedIds.length === 0) return
                onConfirm(selectedIds)
              }}
            >
              开始生成本话
            </ChromePrimaryButton>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {selectableRows.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <ChromeToolbarButton type="button" className="h-8 px-2 text-[11px]" onClick={selectAllGeneratable}>
              全选可生成
            </ChromeToolbarButton>
            <ChromeToolbarButton
              type="button"
              className="h-8 px-2 text-[11px]"
              onClick={selectWithoutApplied}
            >
              仅选未定稿
            </ChromeToolbarButton>
            <ChromeToolbarButton type="button" className="h-8 px-2 text-[11px]" onClick={clearSelection}>
              全不选
            </ChromeToolbarButton>
          </div>
        ) : null}

        {rows.length === 0 ? (
          <p className="typo-lib-empty py-6 text-center text-ink-soft">本话没有分页</p>
        ) : (
          <ul className="space-y-2" role="list">
            {rows.map((row) => {
              const checked = selectedIds.includes(row.pageId)
              return (
                <li key={row.pageId}>
                  <label
                    className={[
                      'flex cursor-pointer gap-3 border border-line bg-panel p-3 transition-colors',
                      cardRound,
                      row.selectable
                        ? checked
                          ? 'ring-1 ring-inset ring-accent/45'
                          : 'hover:bg-[color-mix(in_srgb,var(--panel)_92%,var(--accent)_8%)]'
                        : 'cursor-not-allowed opacity-[0.72]',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 size-4 shrink-0 accent-[var(--accent)]"
                      disabled={!row.selectable}
                      checked={checked}
                      onChange={() => togglePage(row)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-ink">{row.title}</span>
                        {row.pageImageStatus != null ? (
                          <ComicPageImageStatusTag
                            angular={angular}
                            status={row.pageImageStatus}
                          />
                        ) : (
                          <ChromeMetaTag angular={angular}>无画面描述</ChromeMetaTag>
                        )}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-ink-soft">{row.detail}</p>
                    </div>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </ThemeModal>
  )
}
